// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  AnnotationBinding,
  BindingCatalogue,
  NewBusinessTemplate,
  NewBusinessTemplateEditorApi,
} from '@loa/new-business-form';

// Brokerage-scope New Business Application templates for the Advisor UI Provider
// Settings tab. Backed by /api/new-business/templates/** (tenant via
// X-Brokerage-Id, injected by advisorApi). Platform defaults are read-only;
// brokerage overrides are authored by a brokerage ADMIN (the write endpoints are
// ADMIN-gated server-side). See docs/specs/new-business-application-prd.md §5.7.

export type { AnnotationBinding, BindingCatalogue, NewBusinessTemplate };

const base = '/new-business/templates';

export const newBusinessTemplateApi = {
  /** Brokerage's own (BROKERAGE-scope) templates for a provider. */
  list: async (providerId: string): Promise<NewBusinessTemplate[]> =>
    (await api.get(`${base}/provider/${providerId}`)).data,
  /** The platform defaults the brokerage inherits (read-only). */
  listPlatform: async (providerId: string): Promise<NewBusinessTemplate[]> =>
    (await api.get(`${base}/provider/${providerId}/platform`)).data,
  get: async (templateId: string): Promise<NewBusinessTemplate> =>
    (await api.get(`${base}/${templateId}`)).data,
  bindingCatalogue: async (): Promise<BindingCatalogue> =>
    (await api.get(`${base}/binding-catalogue`)).data,

  create: async (body: NewBusinessTemplate): Promise<NewBusinessTemplate> =>
    (await api.post(base, body)).data,
  update: async (templateId: string, body: NewBusinessTemplate): Promise<NewBusinessTemplate> =>
    (await api.put(`${base}/${templateId}`, body)).data,
  activate: async (templateId: string): Promise<NewBusinessTemplate> =>
    (await api.post(`${base}/${templateId}/activate`)).data,
  deactivate: async (templateId: string): Promise<NewBusinessTemplate> =>
    (await api.post(`${base}/${templateId}/deactivate`)).data,
  remove: async (templateId: string): Promise<void> => {
    await api.delete(`${base}/${templateId}`);
  },

  /** Multipart PDF upload — axios sets the multipart boundary from the FormData. */
  uploadPdf: async (templateId: string, file: File): Promise<NewBusinessTemplate> => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post(`${base}/${templateId}/pdf`, form)).data;
  },
  /** The blank provider-form PDF for the annotation editor. */
  fetchPdf: async (templateId: string): Promise<ArrayBuffer> =>
    (await api.get(`${base}/${templateId}/pdf`, { responseType: 'arraybuffer' })).data,
};

/**
 * The subset of the brokerage API the shared {@link NewBusinessTemplateEditor}
 * needs, adapted to its transport-agnostic contract.
 */
export const newBusinessTemplateEditorApi: NewBusinessTemplateEditorApi = {
  fetchPdf: newBusinessTemplateApi.fetchPdf,
  uploadPdf: newBusinessTemplateApi.uploadPdf,
  update: newBusinessTemplateApi.update,
  activate: newBusinessTemplateApi.activate,
  deactivate: newBusinessTemplateApi.deactivate,
  bindingCatalogue: newBusinessTemplateApi.bindingCatalogue,
};
